import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Empty,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined, RobotOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useIsCompact } from "../../hooks/useIsCompact";
import {
  Api,
  hasPermission,
  ISurvey,
  ISurveyLanguageMetadata,
  ISurveyListResponse,
  ISurveyMetadata,
  ISurveyQuestion,
  ISurveyResponse,
  PERMISSIONS,
  SurveyQuestionType,
} from "@model";
import { ORIGIN_LANGUAGES } from "../Character/characterLanguages";

const { TextArea } = Input;
const { Title, Text } = Typography;

const QUESTION_TYPES: { value: SurveyQuestionType; label: string }[] = [
  { value: "radio", label: "Radio — choose one" },
  { value: "check", label: "Check — choose several" },
  { value: "input", label: "Input — one line" },
  { value: "text", label: "Text — multiple lines" },
  { value: "radioOrInput", label: "Radio or own answer" },
];

const choiceType = (type?: string) =>
  type === "radio" || type === "check" || type === "radioOrInput";

const SURVEY_LANGUAGES = ORIGIN_LANGUAGES;

const TRANSLATION_LANGUAGES = SURVEY_LANGUAGES.filter((lang) => lang.key !== "en");

const emptyQuestion = (): ISurveyQuestion => ({
  questionId: crypto.randomUUID(),
  question: "",
  type: "radio",
  options: ["", ""],
});

const filledOptions = (options?: string[]) =>
  (options ?? []).map((option) => String(option ?? "").trim()).filter(Boolean);

const inputDirection = (rtl: boolean) =>
  rtl
    ? { dir: "rtl" as const, style: { direction: "rtl" as const, textAlign: "right" as const } }
    : { dir: "ltr" as const };

const formatAnswer = (value?: string | string[]) => {
  if (Array.isArray(value)) return value.join(", ");
  return value || "-";
};

const userLabel = (user: ISurveyResponse["userID"]) => {
  if (user && typeof user === "object") {
    return user.name || user.email || user._id || "-";
  }
  return user || "-";
};

const userEditPath = (user: ISurveyResponse["userID"]) => {
  const id = user && typeof user === "object" ? user._id : user;
  return id ? `/panel/users/edit/${id}` : null;
};

const SurveyPage = () => {
  const isCompact = useIsCompact();
  const [translationLang, setTranslationLang] = useState("fr");
  const [form] = Form.useForm();
  const [surveys, setSurveys] = useState<ISurvey[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ISurvey | null>(null);
  const [responsesSurvey, setResponsesSurvey] = useState<ISurvey | null>(null);
  const [responses, setResponses] = useState<ISurveyResponse[]>([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesPage, setResponsesPage] = useState(1);
  const [responsesTotal, setResponsesTotal] = useState(0);

  const canCreate = hasPermission(PERMISSIONS.surveysCreate);
  const canEdit = hasPermission(PERMISSIONS.surveysEdit);
  const canDelete = hasPermission(PERMISSIONS.surveysDelete);
  const sourceRtl = Form.useWatch("rtl", form) === true;

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Api.get("/api/admin/panel/surveys", {
        params: { page, limit: 20 },
      });
      const result = response.data?.data as ISurveyListResponse;
      setSurveys(result?.data ?? []);
      setTotal(result?.total ?? 0);
    } catch {
      message.error("Failed to load surveys");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void loadSurveys();
  }, [loadSurveys]);

  const openEditor = (survey?: ISurvey) => {
    setEditing(survey ?? null);
    form.setFieldsValue({
      title: survey?.title ?? "",
      description: survey?.description ?? "",
      isActive: survey?.isActive !== false,
      rtl: survey?.rtl === true,
      metadata: survey?.metadata ?? {},
      questions: survey?.questions?.length
        ? survey.questions.map((question) => ({
            questionId: question.questionId,
            question: question.question,
            type: question.type,
            options: question.options?.length ? question.options : ["", ""],
          }))
        : [emptyQuestion()],
    });
    setEditorOpen(true);
  };

  const saveSurvey = async (values: {
    title: string;
    description?: string;
    isActive?: boolean;
    rtl?: boolean;
    questions: ISurveyQuestion[];
    metadata?: ISurveyMetadata;
  }) => {
    const questions = (values.questions ?? []).map((question) => ({
      questionId: question.questionId || crypto.randomUUID(),
      question: question.question?.trim(),
      type: question.type,
      options: choiceType(question.type)
        ? filledOptions(question.options)
        : undefined,
    }));
    const invalid = questions.find(
      (question) =>
        !question.question ||
        (choiceType(question.type) && (question.options?.length ?? 0) < 2),
    );
    if (invalid) {
      message.error(
        choiceType(invalid.type) && (invalid.options?.length ?? 0) < 2
          ? "Choice questions need at least two options"
          : "Each question needs text",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: values.title.trim(),
        description: values.description?.trim(),
        isActive: values.isActive !== false,
        rtl: values.rtl === true,
        questions,
        metadata: form.getFieldValue("metadata") ?? values.metadata,
      };
      if (editing) {
        await Api.put(`/api/admin/panel/surveys/${editing._id}`, payload);
      } else {
        await Api.post("/api/admin/panel/surveys", payload);
      }
      message.success(editing ? "Survey updated" : "Survey created");
      setEditorOpen(false);
      loadSurveys();
    } catch (error: any) {
      message.error(error?.response?.data?.error?.[0] ?? "Failed to save survey");
    } finally {
      setSaving(false);
    }
  };

  const handleTranslate = async () => {
    const values = form.getFieldsValue(true);
    const questions = ((values.questions ?? []) as ISurveyQuestion[]).map((question) => ({
      questionId: question.questionId,
      question: question.question?.trim() ?? "",
      type: question.type,
      options: choiceType(question.type) ? filledOptions(question.options) : undefined,
    }));
    if (!String(values.title ?? "").trim()) {
      message.error("Fill in the title first");
      return;
    }
    if (questions.some((question) => !question.questionId || !question.question)) {
      message.error("Each question needs text before translating");
      return;
    }
    if (
      questions.some(
        (question) => choiceType(question.type) && (question.options?.length ?? 0) < 2,
      )
    ) {
      message.error("Choice questions need at least two options");
      return;
    }
    setTranslating(true);
    try {
      const response = await Api.post("/api/admin/panel/surveys/translate-metadata", {
        title: String(values.title).trim(),
        description: String(values.description ?? "").trim() || undefined,
        questions,
      });
      const translations = response.data?.data as ISurveyMetadata | undefined;
      if (!translations) {
        message.error("AI returned no translations");
        return;
      }
      const current = (form.getFieldValue("metadata") ?? {}) as ISurveyMetadata;
      const next: ISurveyMetadata = { ...current };
      for (const [lang, fields] of Object.entries(translations)) {
        if (lang === "en" || !fields) continue;
        next[lang] = fields as ISurveyLanguageMetadata;
      }
      form.setFieldsValue({ metadata: next });
      message.success("Translated survey metadata");
    } catch (error: any) {
      message.error(error?.response?.data?.error?.[0] ?? "Failed to translate metadata");
    } finally {
      setTranslating(false);
    }
  };

  const deleteSurvey = (survey: ISurvey) => {
    Modal.confirm({
      title: `Delete “${survey.title}”?`,
      content: "Saved responses for this survey will be removed too.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await Api.delete(`/api/admin/panel/surveys/${survey._id}`);
          message.success("Survey deleted");
          loadSurveys();
        } catch (error: any) {
          message.error(error?.response?.data?.error?.[0] ?? "Failed to delete survey");
        }
      },
    });
  };

  const loadResponses = useCallback(async (survey: ISurvey, nextPage: number) => {
    setResponsesLoading(true);
    try {
      const response = await Api.get(
        `/api/admin/panel/surveys/${survey._id}/responses`,
        { params: { page: nextPage, limit: 20 } },
      );
      const result = response.data?.data;
      setResponses(result?.data ?? []);
      setResponsesTotal(result?.total ?? 0);
      setResponsesPage(nextPage);
    } catch {
      message.error("Failed to load responses");
    } finally {
      setResponsesLoading(false);
    }
  }, []);

  const openResponses = (survey: ISurvey) => {
    setResponsesSurvey(survey);
    setResponses([]);
    void loadResponses(survey, 1);
  };

  const languageLabel = (key?: string) =>
    SURVEY_LANGUAGES.find((lang) => lang.key === key)?.label || key || "-";

  const originLanguageKey = (user: ISurveyResponse["userID"]) => {
    if (!user || typeof user !== "object") return "";
    return (user.originLanguage ?? "").toLowerCase().split("-")[0];
  };

  const tileIsRtl = (user: ISurveyResponse["userID"]) => {
    const key = originLanguageKey(user);
    return key === "fa" || key === "ar";
  };

  return (
    <Card style={{ margin: isCompact ? 0 : 20 }} bodyStyle={isCompact ? { padding: 12 } : undefined}>
      <div
        style={{
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isCompact ? "stretch" : "center",
          gap: 8,
        }}
      >
        <Title level={isCompact ? 4 : 2} style={{ margin: 0 }}>Surveys</Title>
        {canCreate && (
          <Button type="primary" block={isCompact} icon={<PlusOutlined />} onClick={() => openEditor()}>
            New survey
          </Button>
        )}
      </div>
      {isCompact ? (
        <Spin spinning={loading}>
          <div style={{ marginTop: 12 }}>
            {surveys.length === 0 && !loading ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No surveys" />
            ) : (
              surveys.map((survey) => (
                <div
                  key={survey._id}
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{survey.title}</div>
                  <Text type="secondary" copyable={{ text: survey._id }} style={{ fontSize: 12, wordBreak: "break-all" }}>
                    {survey._id}
                  </Text>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "#666" }}>{survey.questions?.length ?? 0} questions</span>
                    <span style={{ fontSize: 12, color: "#666" }}>{survey.responseCount ?? 0} responses</span>
                    {survey.isActive === false ? <Tag>Inactive</Tag> : <Tag color="green">Active</Tag>}
                  </div>
                  <Space size={0} wrap>
                    <Button type="link" style={{ paddingLeft: 0 }} onClick={() => openResponses(survey)}>
                      Responses
                    </Button>
                    {canEdit && (
                      <Button type="link" onClick={() => openEditor(survey)}>Edit</Button>
                    )}
                    {canDelete && (
                      <Button danger type="link" onClick={() => deleteSurvey(survey)}>Delete</Button>
                    )}
                  </Space>
                </div>
              ))
            )}
            {total > 20 && (
              <Pagination
                style={{ marginTop: 8, textAlign: "center" }}
                size="small"
                simple
                current={page}
                pageSize={20}
                total={total}
                onChange={setPage}
              />
            )}
          </div>
        </Spin>
      ) : (
      <Table
        style={{ marginTop: 16 }}
        rowKey="_id"
        loading={loading}
        dataSource={surveys}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "Title",
            dataIndex: "title",
            render: (title: string, survey: ISurvey) => (
              <div>
                <div>{title}</div>
                <Text type="secondary" copyable={{ text: survey._id }} style={{ fontSize: 12 }}>
                  {survey._id}
                </Text>
              </div>
            ),
          },
          {
            title: "Questions",
            dataIndex: "questions",
            width: 120,
            render: (questions: ISurveyQuestion[]) => questions?.length ?? 0,
          },
          {
            title: "Responses",
            dataIndex: "responseCount",
            width: 120,
            render: (count: number) => count ?? 0,
          },
          {
            title: "Status",
            dataIndex: "isActive",
            width: 120,
            render: (isActive: boolean) =>
              isActive === false ? <Tag>Inactive</Tag> : <Tag color="green">Active</Tag>,
          },
          {
            title: "Actions",
            width: 280,
            render: (_: unknown, survey: ISurvey) => (
              <Space>
                <Button type="link" onClick={() => openResponses(survey)}>Responses</Button>
                {canEdit && (
                  <Button type="link" onClick={() => openEditor(survey)}>Edit</Button>
                )}
                {canDelete && (
                  <Button danger type="link" onClick={() => deleteSurvey(survey)}>Delete</Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      )}

      <Modal
        title={editing ? "Edit survey" : "New survey"}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editing ? "Save" : "Create"}
        width={isCompact ? "100%" : 1200}
        maskClosable={false}
        keyboard={false}
        bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={saveSurvey}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input placeholder="Survey title" {...inputDirection(sourceRtl)} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional note shown above the questions" {...inputDirection(sourceRtl)} />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item name="isActive" valuePropName="checked">
              <Checkbox>Active</Checkbox>
            </Form.Item>
            <Form.Item name="rtl" valuePropName="checked">
              <Checkbox>Right to left</Checkbox>
            </Form.Item>
          </Space>
          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <div>
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    size="small"
                    style={{ marginBottom: 12 }}
                    title={`Question ${index + 1}`}
                    extra={
                      fields.length > 1 ? (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      ) : null
                    }
                  >
                    <Form.Item name={[field.name, "questionId"]} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "question"]}
                      label="Question"
                      rules={[{ required: true, message: "Question is required" }]}
                    >
                      <Input placeholder="What do you want to ask?" {...inputDirection(sourceRtl)} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "type"]}
                      label="Type"
                      rules={[{ required: true }]}
                    >
                      <Select options={QUESTION_TYPES} />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, next) =>
                        prev?.questions?.[field.name]?.type !== next?.questions?.[field.name]?.type
                      }
                    >
                      {() =>
                        choiceType(form.getFieldValue(["questions", field.name, "type"])) ? (
                          <Form.List name={[field.name, "options"]}>
                            {(optionFields, optionOps) => (
                              <div>
                                <Text strong>Options</Text>
                                {optionFields.map((optionField, optionIndex) => (
                                  <div
                                    key={optionField.key}
                                    style={{ display: "flex", marginTop: 8, width: "100%", gap: 8, alignItems: "baseline" }}
                                  >
                                    <Form.Item
                                      name={optionField.name}
                                      style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
                                    >
                                      <Input placeholder={`Option ${optionIndex + 1}`} {...inputDirection(sourceRtl)} />
                                    </Form.Item>
                                    {optionFields.length > 2 && (
                                      <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => optionOps.remove(optionField.name)}
                                      />
                                    )}
                                  </div>
                                ))}
                                <Button
                                  type="dashed"
                                  onClick={() => optionOps.add("")}
                                  style={{ marginTop: 8 }}
                                  icon={<PlusOutlined />}
                                >
                                  Add option
                                </Button>
                              </div>
                            )}
                          </Form.List>
                        ) : null
                      }
                    </Form.Item>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add(emptyQuestion())}
                  block
                  icon={<PlusOutlined />}
                >
                  Add question
                </Button>
              </div>
            )}
          </Form.List>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const questions = (form.getFieldValue("questions") ?? []) as ISurveyQuestion[];
              const translateButton = canCreate || canEdit ? (
                <Tooltip title="Translate the title, description, questions, and options">
                  <Button
                    type="text"
                    aria-label="Translate metadata"
                    icon={<RobotOutlined />}
                    loading={translating}
                    onClick={handleTranslate}
                  />
                </Tooltip>
              ) : null;
              return (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Translations</Text>
                  <Tabs
                    style={{ marginTop: 8 }}
                    activeKey={translationLang}
                    onChange={setTranslationLang}
                    tabBarExtraContent={isCompact ? null : translateButton}
                    renderTabBar={
                      isCompact
                        ? () => (
                            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                              <Select
                                value={translationLang}
                                onChange={setTranslationLang}
                                options={TRANSLATION_LANGUAGES.map((lang) => ({
                                  value: lang.key,
                                  label: lang.label,
                                }))}
                                style={{ flex: 1, minWidth: 0 }}
                              />
                              {translateButton}
                            </div>
                          )
                        : undefined
                    }
                    items={TRANSLATION_LANGUAGES.map((lang) => {
                      const langRtl = lang.key === "fa";
                      const dir = inputDirection(langRtl);
                      return {
                        key: lang.key,
                        label: lang.label,
                        forceRender: true,
                        children: (
                        <>
                          <Form.Item name={["metadata", lang.key, "title"]} label="Title">
                            <Input placeholder={`Title in ${lang.label}`} {...dir} />
                          </Form.Item>
                          <Form.Item name={["metadata", lang.key, "description"]} label="Description">
                            <TextArea rows={2} placeholder={`Description in ${lang.label}`} {...dir} />
                          </Form.Item>
                          {questions.map((question, index) => {
                            const questionId = question.questionId;
                            if (!questionId) return null;
                            const options = choiceType(question.type)
                              ? filledOptions(question.options)
                              : [];
                            return (
                              <Card
                                key={questionId}
                                size="small"
                                style={{ marginBottom: 12 }}
                                title={
                                  <span {...inputDirection(sourceRtl)}>
                                    {question.question?.trim() || `Question ${index + 1}`}
                                  </span>
                                }
                              >
                                <Form.Item
                                  name={["metadata", lang.key, "questions", questionId, "question"]}
                                  label="Question"
                                >
                                  <Input {...dir} />
                                </Form.Item>
                                {options.map((option, optionIndex) => (
                                  <Form.Item
                                    key={`${questionId}-${optionIndex}`}
                                    name={["metadata", lang.key, "questions", questionId, "options", optionIndex]}
                                    label={option}
                                  >
                                    <Input {...dir} />
                                  </Form.Item>
                                ))}
                              </Card>
                            );
                          })}
                        </>
                      ),
                      };
                    })}
                  />
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={responsesSurvey ? `Responses — ${responsesSurvey.title}` : "Responses"}
        open={!!responsesSurvey}
        onCancel={() => setResponsesSurvey(null)}
        footer={null}
        width={isCompact ? "100%" : 960}
      >
        <Spin spinning={responsesLoading}>
          {!responsesLoading && responses.length === 0 ? (
            <Empty description="No responses yet" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[...responses]
                .sort((a, b) => {
                  const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                  const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                  return bTime - aTime;
                })
                .map((record) => {
                const rtl = tileIsRtl(record.userID);
                const profilePath = userEditPath(record.userID);
                const label = userLabel(record.userID);
                return (
                  <Card
                    key={record._id}
                    size="small"
                    title={
                      profilePath ? (
                        <Link to={profilePath} style={{ color: "#1890ff" }}>
                          {label}
                        </Link>
                      ) : (
                        label
                      )
                    }
                    style={{
                      width: "100%",
                      ...(rtl ? { direction: "rtl" as const, textAlign: "right" as const } : {}),
                    }}
                  >
                  <Space size="small" style={{ marginBottom: 8 }}>
                    <Tag>{languageLabel(record.language)}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {record.createdAt ? new Date(record.createdAt).toLocaleString() : "-"}
                    </Text>
                  </Space>
                  {(responsesSurvey?.questions ?? []).map((question) => (
                    <div key={question.questionId} style={{ marginTop: 10 }}>
                      <Text type="secondary">{question.question}</Text>
                      <div>
                        {formatAnswer(
                          record.answers?.find((answer) => answer.questionId === question.questionId)
                            ?.value,
                        )}
                      </div>
                    </div>
                    ))}
                  </Card>
                );
              })}
            </div>
          )}
        </Spin>
        <Pagination
          style={{ marginTop: 16, textAlign: isCompact ? "center" : "right" }}
          simple={isCompact}
          current={responsesPage}
          pageSize={20}
          total={responsesTotal}
          onChange={(nextPage) => {
            if (responsesSurvey) void loadResponses(responsesSurvey, nextPage);
          }}
          showSizeChanger={false}
          hideOnSinglePage
        />
      </Modal>
    </Card>
  );
};

export default SurveyPage;
